interface Slide {
  full: string;
  title: string;
  alt: string;
}

/**
 * Gallery lightbox. Uses a native <dialog>, so focus trapping, Esc-to-close and
 * inerting the page behind it come from the platform rather than from script.
 */
export function initLightbox(): void {
  const dialog = document.getElementById('lightbox') as HTMLDialogElement | null;
  const img = document.getElementById('lightbox-img') as HTMLImageElement | null;
  const title = document.getElementById('lightbox-title');
  const count = document.getElementById('lightbox-count');
  const gallery = document.getElementById('gallery');
  if (!dialog || !img || !title || !count || !gallery) return;

  // Bail out rather than render a dead dialog on browsers without <dialog>.
  if (typeof dialog.showModal !== 'function') return;

  const triggers = Array.from(gallery.querySelectorAll<HTMLButtonElement>('button[data-full]'));
  if (triggers.length === 0) return;

  const slides: Slide[] = triggers.map((t) => ({
    full: t.dataset.full ?? '',
    title: t.dataset.title ?? '',
    alt: t.dataset.alt ?? '',
  }));

  let index = 0;

  function show(next: number): void {
    index = (next + slides.length) % slides.length;
    const slide = slides[index];
    img!.src = slide.full;
    img!.alt = slide.alt;
    title!.textContent = slide.title;
    count!.textContent = `${index + 1} / ${slides.length}`;
  }

  triggers.forEach((trigger, i) => {
    trigger.addEventListener('click', () => {
      show(i);
      dialog.showModal();
    });
  });

  document.getElementById('lightbox-close')?.addEventListener('click', () => dialog.close());
  document.getElementById('lightbox-prev')?.addEventListener('click', () => show(index - 1));
  document.getElementById('lightbox-next')?.addEventListener('click', () => show(index + 1));

  dialog.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'ArrowLeft') show(index - 1);
    if (event.key === 'ArrowRight') show(index + 1);
  });

  // Clicking the backdrop closes: the dialog fills the viewport, so any click
  // landing on the dialog itself rather than its contents is outside the image.
  dialog.addEventListener('click', (event: MouseEvent) => {
    if (event.target === dialog) dialog.close();
  });

  // Return focus to the thumbnail that opened the viewer.
  dialog.addEventListener('close', () => triggers[index]?.focus());
}
